import React, { useContext, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';

import useApi from 'common/useApi';
import UiContext from 'common/UiContext/UiContext';
import AuthContext from 'common/auth';

import getHashrateValueWithUnit from 'common/getHashrateValueWithUnit';
import {
  RANGES,
  RANGE_CONFIG,
  RANGE_LABELS,
  HashrateRange,
  buildTimeTicks,
} from 'common/timeAxis';

import IHashRate from 'common/types/IHashRate';
import IHashRateUnits from 'common/types/IHashRateUnits';
import IgraphValuesLinkedList from 'components/HashrateGraph/IgraphValuesLinkedList';

import Icons from 'assets/images/Icons';
import HashrateGraph from 'components/HashrateGraph';

import './recent-hashrate.scss';
import Caption from './Caption';

const RecentHashrate = () => {
  const { addToast } = useContext(UiContext);
  const { user } = useContext(AuthContext)!;
  const timezone = user?.timezone;

  const [range, setRange] = useState<HashrateRange>('24h');
  const [hashrateMaximized, setHashrateMaximized] = useState(true);

  const { data: hashrates, isError, isLoading, isFetching } = useApi(
    'getList',
    '/api/dashboard/hashrates',
    { range },
  );

  const [hashrateUnits, setHashrateUnits] = useState<IHashRateUnits>({
    coefficient: 1,
    magnitude: 1,
    nextUnit: '',
    unit: '',
  });
  const[hashrateSlice, setHashrateSlice] = useState<IHashRate[]>([]);
  const [maxWorkers, setMaxWorkers] = useState(0);
  const [minMaxSteps, setMinMaxSteps] = useState({
    max: 0,
    min: 0,
    steps: 0,
  });

  const [graphVisibleElements, setGraphVisibleElements] = useState<Array<keyof IgraphValuesLinkedList>>([
    'workers_active',
    'hashrate',
    'scoring_hashrate',
  ]);

  // The chart's time domain: a trailing window ending "now". Pinned per fetch so
  // the axis doesn't jitter between renders.
  const { startMs, endMs, bucketMs } = useMemo(() => {
    const end = Date.now();
    const cfg = RANGE_CONFIG[range];

    return { startMs: end - cfg.windowMs, endMs: end, bucketMs: cfg.bucketMs };
  }, [range, hashrates]);

  const ticks = useMemo(
    () => buildTimeTicks(startMs, endMs, range, timezone),
    [startMs, endMs, range, timezone],
  );

  const toggleGraphVisibleElements = (element: keyof IgraphValuesLinkedList) => {
    setGraphVisibleElements((old) => old.includes(element)
      ? old.filter((el) => el !== element)
      : [...old, element],
    );
  };

  const toggleHashrateMaximized = () => {
    setHashrateMaximized((old) => !old);
  };

  const roundToTheCLosestMultipleOfTen = (n: number, roundDown = false) => {
    const digitNumber = Math.pow(10, Math.floor(Math.log10(n)));

    return roundDown ? Math.floor(n / digitNumber) * digitNumber : Math.ceil(n / digitNumber) * digitNumber;
  };

  useEffect(() => {
    setHashrateSlice(hashrates ?? []);
  }, [hashrates]);

  useEffect(() => {
    setHashrateUnits(getHashrateValueWithUnit(Math.max(...hashrateSlice.reduce<Array<number>>(
      (hashrates, { hashrate, scoring_hashrate }) => [
        ...hashrates,
        parseFloat(hashrate),
        parseFloat(scoring_hashrate),
      ]
      , []))));

    setMaxWorkers(Math.max(...hashrateSlice.reduce<Array<number>>(
      (workers, { workers_active }) => [...workers, workers_active], [],
    )));

    setMinMaxSteps(() => {
      let max = 1, min = hashrateMaximized ? 0 : Number.POSITIVE_INFINITY;

      hashrateSlice.forEach(({ hashrate, scoring_hashrate }) => {

        if(parseFloat(hashrate) > max) {
          max = roundToTheCLosestMultipleOfTen(parseFloat(hashrate));
        }
        if(parseFloat(scoring_hashrate) > max) {
          max = roundToTheCLosestMultipleOfTen(parseFloat(scoring_hashrate));
        }
        if(parseFloat(hashrate) < min) {
          min = roundToTheCLosestMultipleOfTen(parseFloat(hashrate), true);
        }
        if(parseFloat(scoring_hashrate) < min) {
          min = roundToTheCLosestMultipleOfTen(parseFloat(scoring_hashrate), true);
        }
      });

      min = hashrateUnits.magnitude > min ? 0 : min;

      min = Number.POSITIVE_INFINITY === min ? 0 : min;
      let steps = parseInt((max - min).toString()[0]);

      steps = isNaN(steps) ? 1 : steps;

      return {
        max,
        min,
        steps,
      };
    });

  }, [hashrateSlice, hashrateMaximized]);

  const hashrateLabel = (iteration: number) => {
    const { max, steps } = minMaxSteps;

    const labelFigure = hashrateMaximized
      ? max / steps * iteration
      : max - hashrateUnits.magnitude * (steps - iteration);
    const valueWithUnits = getHashrateValueWithUnit(labelFigure);

    return labelFigure / valueWithUnits.coefficient + valueWithUnits.unit;
  };

  useEffect(() => {
    if(isError) {
      addToast!('Failed loading data');
    }
  }, [isError]);

  return (
    <div className={classNames({
      box: true,
      isLoading: isLoading || isFetching,
    })}
    >
      <h5>Recent Hashrate</h5>
      <nav className='boxNavigation'>
        <div className='rangeSelector'>
          { RANGES.map((option) => (
            <button
              key={option}
              type='button'
              className={classNames({ active: option === range })}
              onClick={() => setRange(option)}
            >
              {RANGE_LABELS[option]}
            </button>
          )) }
        </div>
        <Icons name={hashrateMaximized ? 'HashrateMax' : 'HashrateMin'} onClick={toggleHashrateMaximized} />
      </nav>
      <div className='hashrate-graph-scaffolding'>
        <div className='unit-names'>
          <span>Hashrate</span>
          <span>Count</span>
        </div>
        <div className='hashrate-values'>
          { 0 < hashrateSlice.length && 0 < minMaxSteps.steps && (
            <>
              { [...Array(minMaxSteps.steps + 1)
                .fill(true)]
                .map((v, k) => (
                  <span key={k}>
                    {hashrateLabel(k) }
                  </span>
                ))}
            </>
          )}
        </div>
        <HashrateGraph
          isLoading={isLoading || isFetching}
          hashrateMaximized={hashrateMaximized}
          hashRatesSlice={hashrateSlice}
          hashrateUnits={hashrateUnits}
          maxWorkers={maxWorkers}
          min={minMaxSteps.min}
          max={minMaxSteps.max}
          graphVisibleElements={graphVisibleElements}
          startMs={startMs}
          endMs={endMs}
          bucketMs={bucketMs}
          ticks={ticks}
          timezone={timezone}
        />
        <div className='count-values'>
          { 0 < hashrateSlice.length && 0 < maxWorkers && (
            [...new Array(maxWorkers + 1).fill(true)].map((v, k) => (
              <span key={k}>{k}</span>
            ))
          )
          }
        </div>
        <div className='hours'>
          { ticks.map((tick, k) => (
            <span key={k} style={{ left: `${tick.fraction * 100}%` }}>{tick.label}</span>
          )) }
        </div>
        <Caption
          graphVisibleElements={graphVisibleElements}
          toggleGraphVisibleElements={toggleGraphVisibleElements}
        />
      </div>
    </div>
  );
};

export default RecentHashrate;
